// ScheduleAssignmentManager.js - Tilldela schemamallar till behandlare
//
// En tilldelning = mall + behandlare + datumintervall + frekvens
// (varje/varannan/var tredje vecka, ISO-veckoparitet). Flera tilldelningar
// kan gälla samma behandlare samtidigt (t.ex. en mall på jämna och en på
// ojämna veckor). Förhandsvisningen visar vilka veckor som träffas.

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getScheduleTemplates,
  getScheduleAssignments,
  createScheduleAssignment,
  updateScheduleAssignment,
  deleteScheduleAssignment,
  getClinicUsers
} from './calendarApi';
import './ScheduleAssignmentManager.css';

// ISO-veckonummer (samma beräkning som backendens date.isocalendar())
export const isoWeek = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

const INTERVAL_LABELS = {
  1: 'Varje vecka',
  2: 'Varannan vecka',
  3: 'Var tredje vecka'
};

const emptyForm = {
  user_id: '',
  template_id: '',
  valid_from: '',
  valid_to: '',
  recurrence_interval: 1,
  week_offset: 0
};

const ScheduleAssignmentManager = () => {
  const [assignments, setAssignments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterUserId, setFilterUserId] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [asg, tpl, usr] = await Promise.all([
        getScheduleAssignments(),
        getScheduleTemplates(),
        getClinicUsers({ calendarOnly: true })
      ]);
      setAssignments(asg);
      setTemplates(tpl.filter(t => t.active));
      setUsers(Array.isArray(usr) ? usr : usr.users || []);
    } catch (err) {
      setError('Kunde inte ladda schematilldelningar');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  // ── Formulär ────────────────────────────────────────────────
  const setField = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      // Håll offset giltigt när frekvensen ändras
      if (field === 'recurrence_interval') {
        const interval = parseInt(value);
        if (next.week_offset >= interval) next.week_offset = 0;
        // Förifyll offset från startdatumets vecka - "schemat börjar den veckan"
        if (interval > 1 && next.valid_from) {
          next.week_offset = isoWeek(new Date(next.valid_from)) % interval;
        }
      }
      if (field === 'valid_from' && prev.recurrence_interval > 1 && value) {
        next.week_offset = isoWeek(new Date(value)) % prev.recurrence_interval;
      }
      return next;
    });
  };

  const handleEdit = (assignment) => {
    setEditingId(assignment.assignment_id);
    setForm({
      user_id: String(assignment.user_id),
      template_id: String(assignment.template_id),
      valid_from: assignment.valid_from || '',
      valid_to: assignment.valid_to || '',
      recurrence_interval: assignment.recurrence_interval,
      week_offset: assignment.week_offset
    });
    setError(null);
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.user_id || !form.template_id || !form.valid_from) {
      setError('Behandlare, mall och startdatum krävs');
      return;
    }
    const payload = {
      user_id: parseInt(form.user_id),
      template_id: parseInt(form.template_id),
      valid_from: form.valid_from,
      valid_to: form.valid_to || null,
      recurrence_interval: parseInt(form.recurrence_interval),
      week_offset: parseInt(form.week_offset)
    };
    try {
      setError(null);
      if (editingId) {
        await updateScheduleAssignment(editingId, payload);
      } else {
        await createScheduleAssignment(payload);
      }
      handleCancel();
      loadAll();
    } catch (err) {
      setError(err.response?.data?.msg || 'Kunde inte spara tilldelningen');
    }
  };

  const handleDelete = async (assignmentId) => {
    if (!window.confirm('Ta bort tilldelningen? Behandlarens schemaslots från mallen försvinner.')) return;
    try {
      await deleteScheduleAssignment(assignmentId);
      loadAll();
    } catch (err) {
      setError('Kunde inte ta bort tilldelningen');
    }
  };

  // ── Vecko-alternativ + förhandsvisning ──────────────────────
  const interval = parseInt(form.recurrence_interval);

  const offsetOptions = useMemo(() => {
    if (interval <= 1) return [];
    const base = form.valid_from ? new Date(form.valid_from) : new Date();
    const options = [];
    for (let offset = 0; offset < interval; offset++) {
      // Hitta de närmaste veckorna med denna paritet för etiketten
      const weeks = [];
      const probe = new Date(base);
      while (weeks.length < 3) {
        if (isoWeek(probe) % interval === offset) weeks.push(isoWeek(probe));
        probe.setDate(probe.getDate() + 7);
      }
      let label = `v. ${weeks.join(', v. ')}...`;
      if (interval === 2) {
        label = `${offset === 0 ? 'Jämna' : 'Ojämna'} veckor (${label})`;
      }
      options.push({ offset, label });
    }
    return options;
  }, [interval, form.valid_from]);

  const previewWeeks = useMemo(() => {
    if (!form.valid_from) return [];
    const start = new Date(form.valid_from);
    const end = form.valid_to ? new Date(form.valid_to) : null;
    const offset = parseInt(form.week_offset);
    const result = [];
    const probe = new Date(start);
    // Gå till måndag i startveckan
    probe.setDate(probe.getDate() - ((probe.getDay() || 7) - 1));
    while (result.length < 5) {
      if (end && probe > end) break;
      if (isoWeek(probe) % interval === offset || interval <= 1) {
        const monday = new Date(probe);
        if (!end || monday <= end) {
          result.push({ week: isoWeek(monday), monday: monday.toISOString().slice(0, 10) });
        }
      }
      probe.setDate(probe.getDate() + 7);
      if (probe.getFullYear() > start.getFullYear() + 2) break; // säkerhetsspärr
    }
    return result;
  }, [form.valid_from, form.valid_to, form.week_offset, interval]);

  const visibleAssignments = filterUserId
    ? assignments.filter(a => a.user_id === parseInt(filterUserId))
    : assignments;

  if (loading) return <div className="sam-loading">Laddar tilldelningar...</div>;

  return (
    <div className="schedule-assignment-manager">
      <div className="sam-header">
        <h2>Schematilldelning</h2>
        <p className="sam-description">
          Lägg en schemamall på en behandlare för en period. Mallar skapas under{' '}
          <Link to="/schedule-templates">Schemamallar</Link>.
        </p>
      </div>

      {error && <div className="sam-error">{error}</div>}

      {/* Formulär */}
      <div className="sam-form-container">
        <h3>{editingId ? 'Redigera tilldelning' : 'Ny tilldelning'}</h3>
        <form onSubmit={handleSubmit} className="sam-form">
          <div className="sam-form-row">
            <div className="sam-form-group">
              <label>Behandlare *</label>
              <select value={form.user_id} onChange={e => setField('user_id', e.target.value)}>
                <option value="">Välj behandlare...</option>
                {users.map(u => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.first_name} {u.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="sam-form-group">
              <label>Schemamall *</label>
              <select value={form.template_id} onChange={e => setField('template_id', e.target.value)}>
                <option value="">Välj mall...</option>
                {templates.map(t => (
                  <option key={t.template_id} value={t.template_id}>{t.name}</option>
                ))}
              </select>
            </div>

            <div className="sam-form-group sam-form-group-small">
              <label>Från *</label>
              <input
                type="date"
                value={form.valid_from}
                onChange={e => setField('valid_from', e.target.value)}
              />
            </div>

            <div className="sam-form-group sam-form-group-small">
              <label>Till (tomt = tills vidare)</label>
              <input
                type="date"
                value={form.valid_to}
                min={form.valid_from || undefined}
                onChange={e => setField('valid_to', e.target.value)}
              />
            </div>
          </div>

          <div className="sam-form-row">
            <div className="sam-form-group sam-form-group-small">
              <label>Frekvens</label>
              <select
                value={form.recurrence_interval}
                onChange={e => setField('recurrence_interval', e.target.value)}
              >
                {[1, 2, 3].map(i => <option key={i} value={i}>{INTERVAL_LABELS[i]}</option>)}
              </select>
            </div>

            {interval > 1 && (
              <div className="sam-form-group">
                <label>Vilka veckor</label>
                <select
                  value={form.week_offset}
                  onChange={e => setField('week_offset', e.target.value)}
                >
                  {offsetOptions.map(o => (
                    <option key={o.offset} value={o.offset}>{o.label}</option>
                  ))}
                </select>
              </div>
            )}

            {previewWeeks.length > 0 && (
              <div className="sam-preview">
                <span className="sam-preview-label">Träffar:</span>
                {previewWeeks.map(w => (
                  <span key={w.monday} className="sam-preview-week" title={`Måndag ${w.monday}`}>
                    v.{w.week}
                  </span>
                ))}
                <span className="sam-preview-more">...</span>
              </div>
            )}
          </div>

          <div className="sam-form-actions">
            <button type="submit" className="btn btn-primary">
              {editingId ? 'Uppdatera' : 'Tilldela'}
            </button>
            {editingId && (
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Avbryt
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Lista */}
      <div className="sam-list">
        <div className="sam-list-header">
          <h3>Aktiva tilldelningar</h3>
          <select value={filterUserId} onChange={e => setFilterUserId(e.target.value)}>
            <option value="">Alla behandlare</option>
            {users.map(u => (
              <option key={u.user_id} value={u.user_id}>
                {u.first_name} {u.last_name}
              </option>
            ))}
          </select>
        </div>

        {visibleAssignments.length === 0 ? (
          <p className="sam-empty">Inga tilldelningar än. Skapa den första ovan!</p>
        ) : (
          <table className="sam-table">
            <thead>
              <tr>
                <th>Behandlare</th>
                <th>Mall</th>
                <th>Period</th>
                <th>Frekvens</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleAssignments.map(a => (
                <tr key={a.assignment_id} className={a.active ? '' : 'sam-row-inactive'}>
                  <td>{a.user_name}</td>
                  <td>{a.template_name}</td>
                  <td>
                    {a.valid_from} – {a.valid_to || 'tills vidare'}
                  </td>
                  <td>
                    {INTERVAL_LABELS[a.recurrence_interval]}
                    {a.recurrence_interval === 2 && (
                      <span className="sam-offset-hint">
                        {' '}({a.week_offset === 0 ? 'jämna' : 'ojämna'})
                      </span>
                    )}
                    {a.recurrence_interval === 3 && (
                      <span className="sam-offset-hint"> (offset {a.week_offset})</span>
                    )}
                  </td>
                  <td>{a.active ? 'Aktiv' : 'Inaktiv'}</td>
                  <td className="sam-actions">
                    <button className="btn-icon" title="Redigera" onClick={() => handleEdit(a)}>
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="btn-icon btn-icon-danger"
                      title="Ta bort"
                      onClick={() => handleDelete(a.assignment_id)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ScheduleAssignmentManager;
